"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePrimaryField = void 0;
const core_1 = require("../core");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const model_1 = require("../model");
const context_1 = require("../context");
const use_meta_1 = require("./use_meta");
const use_field_1 = require("./use_field");
const store_1 = require("../store");
/**
 * Get information of primary field.
 * Rerendering is triggered when a field property changes.
 *
 * @returns primary field info.
 *
 * ### Example
 * ```js
 * import { usePrimaryField, useDatasheet } from '@apitable/widget-sdk';
 *
 * // Show primary field name
 * function PrimaryFieldName() {
 *   const field = usePrimaryField();
 *   return <p>{field.name}</p>
 * }
 *
 * // Show corresponding datasheetId(dstXXXXXXXX) Name of the primary field of the datasheet
 * function DatasheetPrimaryFieldName() {
 *   const useDatasheet = useDatasheet('dstXXXXXXXX');
 *   const field = usePrimaryField(useDatasheet);
 *   return <p>{field.name}</p>
 * }
 * ```
 *
 */
function usePrimaryField(datasheet) {
    const { datasheetId: metaDatasheetId } = (0, use_meta_1.useMeta)();
    const datasheetId = datasheet ? datasheet.datasheetId : metaDatasheetId;
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    const primaryFieldId = (0, react_redux_1.useSelector)(state => {
        var _a;
        return (_a = (0, store_1.getWidgetDatasheet)(state, datasheetId)) === null || _a === void 0 ? void 0 : _a.snapshot.meta.views[0].columns[0].fieldId;
    });
    const fieldMap = (0, react_redux_1.useSelector)(state => {
        return (0, store_1.getFieldMap)(state, datasheetId);
    }, react_redux_1.shallowEqual);
    const fieldPermissionMap = (0, react_redux_1.useSelector)(state => {
        return (0, store_1.getFieldPermissionMap)(state, datasheetId);
    });
    return (0, react_1.useMemo)(() => {
        if (!datasheetId || !fieldMap || !primaryFieldId) {
            return null;
        }
        const fieldRole = core_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, primaryFieldId);
        return new model_1.Field(datasheetId, context, fieldRole === core_1.ConfigConstant.Role.None ? (0, use_field_1.createDeniedField)() : fieldMap[primaryFieldId]);
    }, [datasheetId, fieldMap, primaryFieldId, fieldPermissionMap, context]);
}
exports.usePrimaryField = usePrimaryField;
